import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * Count of paginators currently mounted with more than one page.
 * Arrow-key paging only fires when this is exactly 1 — with several
 * paginated lists on a page there's no unambiguous target, so the
 * shortcut stays out of the way (the user said as much).
 */
let activePaginators = 0;

/**
 * Generic paginator — the single pagination control used across
 * every paginated list in the app.
 *
 * Two modes, one look:
 *
 *   - **URL mode** (default): reads/writes `?page=` on the current
 *     URL via `useNavigate`, so it works on any TanStack-router-owned
 *     route without knowing the route's typed search shape. Renders
 *     real `<a>` anchors with hand-built hrefs so browser
 *     affordances stay (open-in-new-tab, middle-click, keyboard
 *     nav); a click handler intercepts plain left-clicks and routes
 *     through TanStack so navigation stays SPA. `searchParams` is
 *     threaded through the hrefs so filters like `?q=` survive page
 *     changes.
 *
 *   - **Controlled mode**: pass `onPageChange` and the control calls
 *     back instead of touching the URL — for panels that keep page
 *     state in local React state (e.g. a feed embedded in a route
 *     that owns its own search params). Renders `<button>`s.
 *
 * Both modes share the exact same markup and styling so pagination
 * looks identical everywhere.
 */
type BaseProps = {
  total: number;
  page: number;
  pageSize: number;
  /** When set, renders a `Rows: <select>` next to the totals indicator.
   *  Picking a new size resets the page to 1 — otherwise we'd
   *  silently end up on an out-of-range page after a shrink. */
  pageSizes?: number[];
};

type UrlProps = BaseProps & {
  basePath: string;
  searchParams?: Record<string, string | number | undefined>;
  /** Per-route key under which the user's last picked page-size is
   *  remembered. When set, on mount the saved value is restored
   *  (URL navigate, replace=true) provided the URL doesn't already
   *  carry an explicit `?pageSize=`; on change the new value is
   *  written back so the next visit picks it up. Omit for lists
   *  where size shouldn't survive navigation. */
  storageKey?: string;
  onPageChange?: undefined;
  onPageSizeChange?: undefined;
};

type ControlledProps = BaseProps & {
  onPageChange: (page: number) => void;
  /** Controlled-mode counterpart to URL mode's auto-rewrite. Required
   *  when `pageSizes` is passed in controlled mode. */
  onPageSizeChange?: (pageSize: number) => void;
  basePath?: undefined;
  searchParams?: undefined;
};

type PaginationProps = UrlProps | ControlledProps;

/** Canonical set of page sizes — wire into a list page via
 *  `<Pagination pageSizes={PAGE_SIZE_OPTIONS} ...>`. Exported so
 *  every list shares the same options instead of inlining a
 *  literal in each call. */
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const STORAGE_PREFIX = "hub:pageSize:";

function readStoredPageSize(key: string): number | null {
  try {
    const raw = globalThis.localStorage?.getItem(`${STORAGE_PREFIX}${key}`);
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  } catch {
    return null;
  }
}

function writeStoredPageSize(key: string, value: number): void {
  try {
    globalThis.localStorage?.setItem(`${STORAGE_PREFIX}${key}`, String(value));
  } catch {
    // localStorage disabled / full — silent no-op, the URL value
    // still works for the current session.
  }
}

export function Pagination(props: PaginationProps) {
  const { total, page, pageSize } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const multiPage = totalPages > 1;
  // Only URL mode persists page size — `storageKey` doesn't exist on
  // `ControlledProps` at all. Derived once via the same `onPageChange`
  // discriminant `goToPage` above uses, so every later read (effect body,
  // cleanup, dependency array) sees a plain `string | undefined` instead of
  // re-narrowing `props` each time — which a dependency array can't do
  // anyway, since it isn't inside any `if`.
  const storageKey = props.onPageChange ? undefined : props.storageKey;

  const goToPage = (targetPage: number) => {
    if (props.onPageChange) {
      props.onPageChange(targetPage);
      return;
    }
    navigate({
      // TanStack Router strips `undefined` search values from the URL,
      // so page=undefined effectively removes ?page= when we're on
      // page 1, without tripping biome's noDelete rule.
      // @ts-expect-error generic component — TanStack typed routes can't
      // statically validate a pagination search updater.
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: targetPage > 1 ? targetPage : undefined,
      }),
      replace: false,
    });
  };

  // Keep the latest goToPage in a ref so the keydown effect doesn't
  // depend on its (per-render) identity.
  const goToPageRef = useRef(goToPage);
  goToPageRef.current = goToPage;

  // ← / → flip pages, but only when this is the sole paginator on
  // the page (`activePaginators === 1`) and the user isn't typing
  // in a field. Hook runs unconditionally (Rules of Hooks); the
  // body no-ops for single-page lists.
  useEffect(() => {
    if (!multiPage) return;
    activePaginators += 1;
    const onKey = (e: KeyboardEvent) => {
      if (activePaginators !== 1) return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;
      if (e.key === "ArrowLeft" && page > 1) {
        e.preventDefault();
        goToPageRef.current(page - 1);
      } else if (e.key === "ArrowRight" && page < totalPages) {
        e.preventDefault();
        goToPageRef.current(page + 1);
      }
    };
    // `globalThis`, not `window` — the render below shadows `window`
    // with a local number (the ellipsis window size).
    globalThis.addEventListener("keydown", onKey);
    return () => {
      activePaginators -= 1;
      globalThis.removeEventListener("keydown", onKey);
    };
  }, [multiPage, page, totalPages]);

  // Page-size selector visibility — independent of multiPage so a
  // user on a single-page list can still bump the size up (or shrink
  // it after a filter narrows the result set).
  const sizeOptions = props.pageSizes ?? null;
  const showSizeSelector = sizeOptions !== null && sizeOptions.length > 0;

  function setPageSize(next: number) {
    if (next <= 0 || next === pageSize) return;
    if (props.onPageChange) {
      // Controlled mode: `onPageSizeChange` is the documented requirement
      // when `pageSizes` is passed here, but it's optional on the type —
      // if a caller omitted it there's nothing safe to do. Falling through
      // to the URL-mode code below would `navigate()` an app that isn't
      // URL-driven for this pagination instance.
      props.onPageSizeChange?.(next);
      return;
    }
    if (storageKey) writeStoredPageSize(storageKey, next);
    navigate({
      // @ts-expect-error generic component — TanStack typed routes can't
      // statically validate a page-size search updater.
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        pageSize: next,
        page: undefined,
      }),
      replace: false,
    });
  }

  // Restore the user's previously-picked page-size when the URL
  // doesn't carry an explicit `?pageSize=`. Replaces the URL (so
  // back-button doesn't bounce through the default-size view) and
  // only fires once per mount — the navigate triggers a re-render
  // with the stored value already applied, so the second pass sees
  // the URL param and no-ops.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    if (props.onPageChange) return;
    if (!storageKey) return;
    const urlHasPageSize = new URLSearchParams(globalThis.location?.search ?? "").has("pageSize");
    if (urlHasPageSize) {
      restoredRef.current = true;
      return;
    }
    const stored = readStoredPageSize(storageKey);
    if (stored === null || stored === pageSize) {
      restoredRef.current = true;
      return;
    }
    restoredRef.current = true;
    navigate({
      // @ts-expect-error generic component — TanStack typed routes can't
      // statically validate a page-size search updater.
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        pageSize: stored,
        page: undefined,
      }),
      replace: true,
    });
  }, [storageKey, props.onPageChange, pageSize, navigate]);

  if (!multiPage && !showSizeSelector) return null;

  const buildHref = (targetPage: number): string => {
    if (props.onPageChange) return "#";
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(props.searchParams ?? {})) {
      if (k === "page") continue;
      if (v !== undefined && v !== "") params.set(k, String(v));
    }
    if (targetPage > 1) params.set("page", String(targetPage));
    const qs = params.toString();
    return qs ? `${props.basePath}?${qs}` : props.basePath;
  };

  const prev = Math.max(1, page - 1);
  const next = Math.min(totalPages, page + 1);

  const window = 2;
  const pages: (number | "ellipsis")[] = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - window && i <= page + window)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "ellipsis") {
      pages.push("ellipsis");
    }
  }

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);
  const controlled = props.onPageChange !== undefined;

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3">
      <div className="flex items-center gap-3">
        <p className="text-xs text-muted-foreground">
          <Trans
            i18nKey="ui.showing"
            values={{ from: startItem, to: endItem, total }}
            components={{ 1: <span className="font-medium text-foreground" /> }}
          />
        </p>
        {showSizeSelector && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {t("ui.rows")}
            <Select
              size="sm"
              aria-label={t("ui.rowsPerPage")}
              value={String(pageSize)}
              onChange={(v) => setPageSize(Number(v))}
              options={sizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
            />
          </span>
        )}
      </div>
      {multiPage ? (
        <div className="flex items-center gap-1">
          <PageLink
            href={buildHref(prev)}
            controlled={controlled}
            disabled={page === 1}
            onActivate={() => goToPage(prev)}
            ariaLabel={t("ui.previousPage")}
          >
            <ChevronLeft className="h-4 w-4" />
          </PageLink>

          {pages.map((p, i) =>
            p === "ellipsis" ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: ellipsis position is its only stable identity here
              <span key={`e-${i}`} className="px-2 text-xs text-muted-foreground">
                …
              </span>
            ) : (
              <PageLink
                key={p}
                href={buildHref(p)}
                controlled={controlled}
                active={p === page}
                onActivate={() => goToPage(p)}
                ariaLabel={t("ui.page", { page: p })}
              >
                {p}
              </PageLink>
            ),
          )}

          <PageLink
            href={buildHref(next)}
            controlled={controlled}
            disabled={page === totalPages}
            onActivate={() => goToPage(next)}
            ariaLabel={t("ui.nextPage")}
          >
            <ChevronRight className="h-4 w-4" />
          </PageLink>
        </div>
      ) : null}
    </div>
  );
}

const PAGE_LINK_CLS =
  "inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-xs font-medium transition-colors";

function pageLinkClass(active: boolean, disabled: boolean): string {
  return cn(
    PAGE_LINK_CLS,
    active
      ? "border-primary bg-primary text-primary-foreground"
      : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
    disabled && "pointer-events-none opacity-40",
  );
}

function PageLink({
  href,
  controlled,
  active = false,
  disabled = false,
  onActivate,
  ariaLabel,
  children,
}: {
  href: string;
  controlled: boolean;
  active?: boolean;
  disabled?: boolean;
  onActivate: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  // Controlled mode keeps page state in local React state — there's
  // no meaningful href, so render a real button.
  if (controlled) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        aria-current={active ? "page" : undefined}
        disabled={disabled}
        onClick={() => {
          if (!disabled) onActivate();
        }}
        className={pageLinkClass(active, disabled)}
      >
        {children}
      </button>
    );
  }

  return (
    <a
      href={href}
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      aria-disabled={disabled || undefined}
      onClick={(e) => {
        // Let modified clicks (cmd/ctrl/shift + middle button) fall
        // through to the browser so "open in new tab" works.
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        if (!disabled) onActivate();
      }}
      className={pageLinkClass(active, disabled)}
    >
      {children}
    </a>
  );
}
